import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Control, Errors, actions } from 'react-redux-form';
import PropTypes from 'prop-types';

import {
  getValidations,
  getValidationMessages,
} from '../../../validators/validators';
import config from '../../../reducers/config';
import { getFetchFunction } from '../../../bootstrap';

const NUM_INPUTS = 6;

const formatPhoneForDisplay = (phone) => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
      6,
      10
    )}`;
  }
  return phone;
};

const toFixedOtp = (value, length) =>
  typeof value === 'string' ? value.padEnd(length, ' ') : ' '.repeat(length);

const otpDigitsFromValue = (value, length) =>
  Array.from({ length }, (_, i) => (value[i] === ' ' ? '' : value[i] || ''));

const updateOtpAtIndex = (value, index, char, length) => {
  const fixedOtpValue = toFixedOtp(value, length);
  return fixedOtpValue
    .split('')
    .map((v, i) => (i === index ? char : v))
    .join('');
};

const clearOtpAtIndex = (value, index, length) =>
  updateOtpAtIndex(value, index, ' ', length);

const isOtpComplete = (value) => !toFixedOtp(value, NUM_INPUTS).includes(' ');

const controlHasError = (field = {}) => {
  const {
    valid,
    retouched,
    focus,
    touched,
    pristine,
    submitFailed,
    submitted,
  } = field;

  if (!field) return false;
  const propertyEdited = touched || retouched;
  const propertySubmitted = submitFailed || submitted;
  const isStepLoad = !touched && submitted;

  return (
    !isStepLoad &&
    propertyEdited &&
    !pristine &&
    !valid &&
    (!focus || propertySubmitted)
  );
};

const VerifiedServiceOtpInput = ({
  step,
  dispatch,
  handleNext,
  handleSkipStep,
  formData,
  formMeta,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState(null);
  const inputRefs = useRef([]);
  const instructionText =
    step.otpInstructionText || 'Please enter the six digit code we sent';
  const attributeId = step.attributeid || 'VerificationCode';
  const resendCodeText = step.otpResendCodeText || 'Resend Code';
  const phoneNumber = formData?.PhoneNumber || '';
  const maskedPhone = formatPhoneForDisplay(phoneNumber);

  const rawValue = formData?.[attributeId];
  const fixedOtpValue = toFixedOtp(rawValue, NUM_INPUTS);
  const digits = otpDigitsFromValue(fixedOtpValue, NUM_INPUTS);

  const isResendDisabled = isResending || resendError !== null;

  const handleResendCode = async () => {
    if (isResending || resendError) return;

    setIsResending(true);

    dispatch(actions.change(`formData.${attributeId}`, ''));
    dispatch(actions.resetValidity(`formData.${attributeId}`));
    dispatch(actions.setTouched(`formData.${attributeId}`, false));
    dispatch(actions.setPristine(`formData.${attributeId}`, true));

    requestAnimationFrame(() => {
      inputRefs.current[0]?.focus();
    });

    const requestBody = {
      phoneNumber: phoneNumber,
      otpDeliveryMethod: 'SMS',
      FormGuid: formMeta?.formUuid || '',
    };

    const url = `${config.apiConfig.rootApiServerPath}/api/enrich/sendOtp`;

    try {
      const response = await getFetchFunction()(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok || data.errorMessage) {
        setResendError(data.errorMessage || 'Failed to resend code.');
      }
    } catch {
      setResendError('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyAnotherWay = () => {
    if (handleSkipStep) {
      handleSkipStep();
    }
  };

  const submitIfComplete = (currentOtpValue) => {
    const fixed = toFixedOtp(currentOtpValue, NUM_INPUTS);
    if (isOtpComplete(fixed)) {
      handleNext?.();
    }
  };

  const renderOtpInput = ({ value, onChange, fieldValue, inputRefs }) => {
    const hasError = controlHasError(fieldValue);

    const refocus = (i) =>
      requestAnimationFrame(() => inputRefs.current[i]?.focus());

    return (
      <div className="verified-service-otp-input-group">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            autoFocus={index === 0}
            onChange={(e) => {
              const d = e.target.value.replace(/\D/g, '').slice(-1);
              const next = updateOtpAtIndex(
                fixedOtpValue,
                index,
                d,
                NUM_INPUTS
              );

              onChange(next);
              dispatch(actions.resetValidity(`formData.${attributeId}`));
              if (d && index < NUM_INPUTS - 1) {
                requestAnimationFrame(() => {
                  inputRefs.current[index + 1]?.focus();
                });
              }
              if (d && index === NUM_INPUTS - 1) {
                setTimeout(() => {
                  submitIfComplete(next);
                }, 0);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitIfComplete(value);
              } else if (e.key !== 'Backspace') {
                return;
              }
              e.preventDefault();

              const fixed = fixedOtpValue;
              const hasValue = digits[index] !== '';

              if (hasValue) {
                const next = clearOtpAtIndex(fixed, index, NUM_INPUTS);
                onChange(next);
                refocus(index);
                return;
              }

              if (!hasValue && index > 0) {
                refocus(index - 1);
              }
            }}
            onBlur={(e) => {
              if (
                e.relatedTarget &&
                inputRefs.current.includes(e.relatedTarget)
              ) {
                return;
              }
              submitIfComplete(value);
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = e.clipboardData
                .getData('text')
                .replace(/\D/g, '')
                .slice(0, 6);

              if (!pasted) return;

              onChange(pasted);
              inputRefs.current[Math.min(pasted.length - 1, 5)]?.focus();

              if (pasted.length === 6) {
                submitIfComplete(pasted);
              }
            }}
            className={`verified-service-otp-input${
              hasError ? ' verified-service-otp-input--error' : ''
            }`}
            aria-invalid={hasError}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="verified-service-otp-container">
      <p className="verified-service-otp-instruction">
        {instructionText}
        {maskedPhone ? (
          <>
            <br className="verified-service-otp-mobile-break" /> to{' '}
            {maskedPhone}.
          </>
        ) : (
          '.'
        )}
      </p>

      <Control
        model={`formData.${attributeId}`}
        validators={getValidations(step.validation)}
        component={(props) =>
          renderOtpInput({ ...props, inputRefs, handleNext })
        }
        mapProps={{
          fieldValue: (props) => props.fieldValue,
        }}
      />

      <Errors
        model={`formData.${attributeId}`}
        show={(field) => field.touched && !field.valid}
        messages={getValidationMessages(step.validation)}
        component={(props) => (
          <p className="verified-service-otp-error-message">{props.children}</p>
        )}
      />

      {resendError && (
        <p className="verified-service-otp-error-message">{resendError}</p>
      )}

      <div className="verified-service-otp-branding">
        1-click signup powered by{' '}
        <img
          src="/form/assets/images/logos/verified-service-logo.svg"
          alt="Verified"
          className="verified-service-logo"
          width="66"
          height="16"
        />
      </div>

      <div className="verified-service-otp-actions">
        <button
          type="button"
          className={`verified-service-otp-link${
            resendError ? ' verified-service-otp-link--disabled' : ''
          }`}
          onClick={handleResendCode}
          disabled={isResendDisabled}
          style={{ position: 'relative', display: 'inline-block' }}
        >
          <span
            style={{
              opacity: isResending || resendError ? 0.5 : 1,
              color: isResending || resendError ? '#999' : undefined,
            }}
          >
            {resendCodeText}
          </span>
          {isResending && (
            <img
              src="/form/assets/images/misc/spinner.gif"
              alt=""
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '28px',
                height: '28px',
              }}
            />
          )}
        </button>

        <span className="verified-service-otp-separator">or</span>

        <button
          type="button"
          className="verified-service-otp-link"
          onClick={handleVerifyAnotherWay}
        >
          Verify Another Way
        </button>
      </div>
    </div>
  );
};

VerifiedServiceOtpInput.propTypes = {
  step: PropTypes.object.isRequired,
  handleNext: PropTypes.func.isRequired,
  handleSkipStep: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  formData: PropTypes.object,
  formMeta: PropTypes.object,
};

const mapStateToProps = (state) => ({
  formData: state.formData,
  formMeta: state.formMeta,
});

export default connect(mapStateToProps)(VerifiedServiceOtpInput);
